import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  chronicleAmplitudeSourceAtom,
  chronicleAudioRmsAtom,
  chronicleConnectionStatusAtom,
  chronicleDeviceRecordingInfoAtom,
  chronicleIsPausedAtom,
} from '@/atoms/chronicle';

import { BarsBase } from './BarsBase';
import { setBarHeight } from './barTransform';
import {
  CHRONICLE_BARS_TUNING,
  advanceNoiseFloor,
  driveFromRms,
} from './chronicleBarsTuning';

/**
 * Real-time audio visualization for Chronicle input.
 *
 * Converts a single `chronicleAudioRmsAtom.rms` scalar (0.0–1.0) into 5 bar
 * heights using the same symmetric arc ratios as the iOS `HHBarsVolumeIndicator`:
 *   [1/6, 4/6, 6/6, 4/6, 1/6]
 *
 * **Important**: 4.x streams MP3-byte RMS (narrow ~0.35–0.55 band); FW 5.0
 * replays `MCU&LVL` linear PCM peaks — per-source tuning serves both.
 *
 * Freezes the visualization when the Chronicle recording is paused.
 */

const BAR_WEIGHTS = [1 / 6, 4 / 6, 1, 4 / 6, 1 / 6] as const;

const SIZE_CONFIG = {
  default: { minHeight: 4, maxHeight: 20 },
  compact: { minHeight: 3, maxHeight: 12 },
} as const;

interface Props {
  className?: string;
  size?: 'default' | 'compact';
  barCount?: 3 | 5;
}

export const ChronicleBars = ({ className, size = 'default', barCount = 5 }: Props) => {
  const barsRef = useRef<HTMLDivElement[]>([]);
  const { rms } = useAtomValue(chronicleAudioRmsAtom);
  const isPaused = useAtomValue(chronicleIsPausedAtom);
  const source = useAtomValue(chronicleAmplitudeSourceAtom);
  const recordingInfo = useAtomValue(chronicleDeviceRecordingInfoAtom);
  const connectionStatus = useAtomValue(chronicleConnectionStatusAtom);
  const tuning = CHRONICLE_BARS_TUNING[source];
  // Identity of the LVL subscription — it changes on every edge 3/11
  // resubscribes on. The 4.x stream has no edges, so its floor never resets.
  const lvl = `${recordingInfo?.sessionId ?? ''}:${connectionStatus}:${isPaused}`;
  const subscription = source === 'levelMeter' ? lvl : 'stream';

  // Running noise floor estimate (EMA of min RMS during silence).
  const floorRef = useRef<number | null>(null);
  const subscriptionSeenRef = useRef(false);

  useEffect(() => {
    // Drop the floor at a subscription edge; the next frame reseeds it, and
    // seeding renders zero drive, so the bars fall to rest with it.
    if (subscriptionSeenRef.current) floorRef.current = null;
    subscriptionSeenRef.current = true;
  }, [subscription]);

  // A new RAF is requested on every `rms` change from the Jotai atom (~20 fps).
  // This is intentional: each frame reads the latest value and applies the
  // adaptive noise-floor EMA, so skipping frames would break the smoothing.
  // The floorAlpha constant ensures visual stability despite high-frequency
  // updates. A persistent RAF loop reading from a ref was considered but adds
  // complexity without measurable benefit — the atom already throttles to the
  // Rust-side broadcast rate (~50 ms intervals).
  const { minHeight, maxHeight } = SIZE_CONFIG[size];
  // With 3 bars, render the centre three weights of the symmetric arc so the
  // mid-tall-mid shape is preserved.
  const weightOffset = barCount === 3 ? 1 : 0;

  useEffect(() => {
    if (isPaused) return;

    const rafId = requestAnimationFrame(() => {
      floorRef.current = advanceNoiseFloor(floorRef.current, rms, tuning);
      const amplified = driveFromRms(rms, floorRef.current, tuning);

      barsRef.current.forEach((bar, index) => {
        if (!bar) return;
        const weight = BAR_WEIGHTS[index + weightOffset] ?? 0;
        const height = minHeight + amplified * weight * (maxHeight - minHeight);
        setBarHeight(bar, height, size);
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [rms, isPaused, minHeight, maxHeight, weightOffset, subscription, tuning, size]);

  return (
    <BarsBase
      barsRef={barsRef}
      audioReady
      size={size}
      barCount={barCount}
      className={className}
    />
  );
};
